import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
    DefinedRange,
    defaultStaticRanges,
    createStaticRanges,
    DateRange,
} from "@atefbena/react-date-range";
import i18n from "i18next";
import frLocale from "date-fns/locale/fr";
import enUsLocale from "date-fns/locale/en-US";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "reactstrap";

import "@atefbena/react-date-range/dist/styles.css";
import "@atefbena/react-date-range/dist/theme/default.css";

import { getAdvancedPeriodDate } from "../../../store/features/advancedDate";
import { changePeriod } from "../../../store/features/period";
import { getAdvancedSeeMorePeriod as getAdvancedPeriod } from "../../../helpers/general";

import "./index.css";

type PropsType = {
    toggleOpeningModal: () => void;
};

export function MobileDateRangePickerWrapper({
    toggleOpeningModal,
}: PropsType) {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const dispatch = useDispatch();

    const { advancedDate, periodSlice } = useSelector((state: any) => state);

    const [state, setState] = useState<any[]>([
        {
            startDate: new Date(
                Number(
                    getAdvancedPeriod(periodSlice.date, advancedDate).startDate
                ) * 1000
            ),
            endDate: new Date(
                Number(
                    getAdvancedPeriod(periodSlice.date, advancedDate).endDate
                ) * 1000
            ),
            key: "selection",
            label: "",
        },
    ]);

    const [isModalOpened, setIsModalOpened] = useState<boolean>(false);

    // Memoize staticRanges to prevent unnecessary recalculations on each render
    const staticRanges = useMemo(() => {
        const translatedDefaultStaticRanges = defaultStaticRanges
            .filter(
                (range: any) =>
                    ![
                        "Last Year",
                        "This Week",
                        "This Month",
                        "Last Month",
                        "Yesterday",
                    ].includes(range.label)
            )
            .map(({ label, range, isSelected }: any) => ({
                label: t(label),
                range,
                isSelected,
            }));

        return createStaticRanges([
            ...translatedDefaultStaticRanges,
            {
                label: t("Today"),
                range: () => ({
                    startDate: moment().startOf("day").toDate(),
                    endDate: moment().endOf("day").toDate(),
                }),
            },
            {
                label: t("The last 7 days"),
                range: () => ({
                    startDate: moment().subtract(7, "days").toDate(),
                    endDate: moment().subtract(1, "days").toDate(),
                }),
            },
            {
                label: t("The last 30 days"),
                range: () => ({
                    startDate: moment().subtract(30, "days").toDate(),
                    endDate: moment().subtract(1, "days").toDate(),
                }),
            },
            {
                label: t("This Week"),
                range: () => ({
                    startDate: moment().startOf("week").toDate(),
                    endDate: moment().endOf("week").toDate(),
                }),
            },
            {
                label: t("Last Week"),
                range: () => ({
                    startDate: moment()
                        .subtract(1, "week")
                        .startOf("week")
                        .toDate(),
                    endDate: moment()
                        .subtract(1, "week")
                        .endOf("week")
                        .toDate(),
                }),
            },
            {
                label: t("This Month"),
                range: () => ({
                    startDate: moment().startOf("month").toDate(),
                    endDate: moment().endOf("month").toDate(),
                }),
            },
            {
                label: t("Last Month"),
                range: () => ({
                    startDate: moment()
                        .subtract(1, "month")
                        .startOf("month")
                        .toDate(),
                    endDate: moment()
                        .subtract(1, "month")
                        .endOf("month")
                        .toDate(),
                }),
            },
            {
                label: t("This Year"),
                range: () => ({
                    startDate: moment().startOf("year").toDate(),
                    endDate: moment().endOf("year").toDate(),
                }),
            },
            {
                label: t("Personalized"),
                range: () => ({
                    startDate:
                        advancedDate.startDate !== ""
                            ? new Date(Number(advancedDate.startDate) * 1000)
                            : moment().subtract(1, "days").toDate(),
                    endDate:
                        advancedDate.endDate !== ""
                            ? new Date(Number(advancedDate.endDate) * 1000)
                            : new Date(),
                }),
            },
        ]);
    }, [t, advancedDate]);

    // Effect to handle language change in moment
    useEffect(() => {
        moment.locale(i18n.language);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n.language]);

    const toggleOpeningCalendarModal = () => {
        setIsModalOpened((prevState) => !prevState);
    };

    const handleOnChangeEvent = (item: any) => {
        setState([item.selection]);

        if (item.selection.label === t("Personalized")) {
            setIsModalOpened(true);
        }
    };

    const handleCalendarOnChangeEvent = (item: any) => {
        setState([item.selection]);
    };

    // Moved the validation logic into a separate function to prevent re-creating the function inline
    const handleValidateOnClickEvent = () => {
        const formatDate = (date: Date) => moment(date).format("DD/MM/YYYY");

        const startDateFormatted = formatDate(state[0].startDate);
        const endDateFormatted = formatDate(state[0].endDate);

        const selectedRangeIndexInStaticRange = staticRanges.findIndex(
            (range: any) => {
                const rangeStartFormatted = formatDate(range.range().startDate);
                const rangeEndFormatted = formatDate(range.range().endDate);

                return (
                    rangeStartFormatted === startDateFormatted &&
                    rangeEndFormatted === endDateFormatted
                );
            }
        );

        const PERSONALIZED_RANGE_INDEX_IN_STATIC_RANGE =
            pathname === "/statistics-out-of-stock" ? 9 : 11;

        if (
            selectedRangeIndexInStaticRange === -1 ||
            selectedRangeIndexInStaticRange ===
                PERSONALIZED_RANGE_INDEX_IN_STATIC_RANGE
        ) {
            dispatch(
                getAdvancedPeriodDate({
                    startDate: String(moment(state[0].startDate).unix()),
                    endDate: String(moment(state[0].endDate).unix()),
                })
            );
            dispatch(changePeriod(t("Personalized")));
        } else {
            dispatch(
                getAdvancedPeriodDate({
                    startDate: "",
                    endDate: "",
                })
            );
            dispatch(
                changePeriod(
                    staticRanges[selectedRangeIndexInStaticRange]?.label
                )
            );
        }

        toggleOpeningModal();
        toggleOpeningCalendarModal();
    };

    return (
        <>
            <DefinedRange
                onChange={handleOnChangeEvent}
                ranges={state}
                staticRanges={staticRanges}
                locale={i18n.language === "fr" ? frLocale : enUsLocale}
                className="defined-range-width__clz input-ranges-display-none__clz mb-2"
            />
            <div className="d-flex justify-content-center mb-2">
                <Button
                    type="button"
                    color="dark"
                    className="w-50 ml-3"
                    outline
                    onClick={toggleOpeningModal}
                >
                    {t("Cancel")}
                </Button>
                <Button
                    type="button"
                    color="dark"
                    onClick={handleValidateOnClickEvent} // No inline function here
                    className="mx-3 w-50"
                >
                    {t("Validate")}
                </Button>
            </div>
            <Modal
                isOpen={isModalOpened}
                toggle={toggleOpeningCalendarModal}
                centered
                fade={false}
            >
                <DateRange
                    editableDateInputs
                    onChange={handleCalendarOnChangeEvent}
                    moveRangeOnFirstSelection={false}
                    locale={i18n.language === "fr" ? frLocale : enUsLocale}
                    ranges={state}
                />
                <div className="d-flex justify-content-center mb-2">
                    <Button
                        type="button"
                        className="w-50 ml-3"
                        color="dark"
                        outline
                        onClick={toggleOpeningCalendarModal}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        type="button"
                        color="dark"
                        onClick={handleValidateOnClickEvent}
                        className="mx-3 w-50"
                    >
                        {t("Validate")}
                    </Button>
                </div>
            </Modal>
        </>
    );
}
